import React, { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Brain,
  Eye,
  Cpu,
  Activity,
  Users,
  Shuffle,
  Heart,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Clock
} from 'lucide-react';

const ReviewGate = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = () => {
    if (rejectReason.trim()) {
      onReject(rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
    }
  };

  if (execution.status === 'approved') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">Execution approvata</span>
        </div>
      </div>
    );
  }

  if (execution.status === 'rejected') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Execution rigettata</span>
        </div>
        {execution.rejectionReason && (
          <p className="text-sm text-red-600 mt-2">{execution.rejectionReason}</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-800 mb-3">Revisiona Output</h3>
        <div className="flex gap-3">
          <button onClick={() => onApprove()} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
            <CheckCircle2 className="w-4 h-4" /> Approva
          </button>
          <button onClick={onRegenerate} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
            <RefreshCw className="w-4 h-4" /> Rigenera
          </button>
          <button onClick={() => setShowRejectModal(true)} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">
            <XCircle className="w-4 h-4" /> Rigetta
          </button>
        </div>
      </div>

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Motivo del Rigetto</h3>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Spiega perché rigetti questa execution..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows={4} />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Annulla
              </button>
              <button onClick={handleReject} disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                Conferma Rigetto
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const SubModelCard = ({ icon: Icon, title, model = {}, colorClass, bgClass, borderClass }) => {
  const [expanded, setExpanded] = useState(true);
  const entries = Object.entries(model).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (!entries.length) return null;

  return (
    <div className={`rounded-lg border ${borderClass} overflow-hidden`}>
      <button onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between p-3 ${bgClass} hover:opacity-90 transition-opacity`}>
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${colorClass}`} />
          <span className={`text-xs font-semibold uppercase tracking-wide ${colorClass}`}>{title}</span>
        </div>
        {expanded
          ? <ChevronUp className={`w-4 h-4 ${colorClass} opacity-50`} />
          : <ChevronDown className={`w-4 h-4 ${colorClass} opacity-50`} />}
      </button>
      {expanded && (
        <div className="bg-white p-4 space-y-2 border-t border-gray-100">
          {entries.map(([key, value]) => (
            <div key={key}>
              <p className="text-xs font-medium text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
              {Array.isArray(value) ? (
                <ul className="mt-1 space-y-1">
                  {value.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className={`${colorClass} mt-0.5 font-bold`}>·</span>
                      {typeof item === 'string' ? item : JSON.stringify(item)}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-800 mt-0.5">{String(value)}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Contract14AView({ execution, onApprove, onReject, onRegenerate, loading }) {
  if (!execution) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-gray-500">
          <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium mb-2">In attesa di esecuzione</p>
          <p className="text-sm">Clicca "Esegui Contract" per avviare il Behavior Generative Engine</p>
        </div>
      </div>
    );
  }

  const output = execution.output || execution.outputData || {};
  const bg = output.behavioral_generator || {};

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto space-y-6">

        <div>
          <div className="flex items-center gap-3 mb-1">
            <Brain className="w-6 h-6 text-violet-600" />
            <h2 className="text-xl font-bold text-gray-800">Human Behavior Generative Engine</h2>
            <span className="px-2 py-0.5 text-xs bg-violet-100 text-violet-700 rounded-full font-medium">C14A</span>
          </div>
          <p className="text-sm text-gray-500">
            Modello generativo latente del comportamento umano — stati interni che generano il comportamento osservabile
          </p>
        </div>

        <div className="space-y-4">
          <SubModelCard icon={Eye} title="Attention Model" model={bg.attention_model || {}}
            colorClass="text-blue-700" bgClass="bg-blue-50" borderClass="border-blue-200" />
          <SubModelCard icon={Cpu} title="Cognitive Processing Model" model={bg.cognitive_processing_model || {}}
            colorClass="text-indigo-700" bgClass="bg-indigo-50" borderClass="border-indigo-200" />
          <SubModelCard icon={Activity} title="Motor Generation Model" model={bg.motor_generation_model || {}}
            colorClass="text-violet-700" bgClass="bg-violet-50" borderClass="border-violet-200" />
          <SubModelCard icon={Users} title="Social Behavior Model" model={bg.social_behavior_model || {}}
            colorClass="text-purple-700" bgClass="bg-purple-50" borderClass="border-purple-200" />
          <SubModelCard icon={Shuffle} title="Behavioral Variability Model" model={bg.behavioral_variability_model || {}}
            colorClass="text-fuchsia-700" bgClass="bg-fuchsia-50" borderClass="border-fuchsia-200" />
          <SubModelCard icon={Heart} title="Emotional Regulation Model" model={bg.emotional_regulation_model || {}}
            colorClass="text-pink-700" bgClass="bg-pink-50" borderClass="border-pink-200" />
          <SubModelCard icon={MessageCircle} title="Interaction Model" model={bg.interaction_model || {}}
            colorClass="text-rose-700" bgClass="bg-rose-50" borderClass="border-rose-200" />
        </div>

        <ReviewGate
          execution={execution}
          onApprove={onApprove}
          onReject={onReject}
          onRegenerate={onRegenerate}
          loading={loading}
        />

      </div>
    </div>
  );
}
